import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Books, Plus, User, PencilSimple, Trash, GraduationCap, Lock, CaretDown, CaretRight, FolderOpen } from "@phosphor-icons/react";
import { toast } from "sonner";

function CourseCard({ course, idx, canCreate, navigate, handleDeleteCourse, thumbnails, enrolled }) {
  // Round 35 — save scroll on click so we can restore on return.
  const saveScroll = () => {
    try { sessionStorage.setItem("courses_scrollY", String(window.scrollY)); } catch (_) {}
  };
  return (
    <div className="relative group/card">
      <Link to={`/courses/${course.id}`} onClick={saveScroll} data-testid={`course-card-${idx}`}>
        <Card className={`border rounded-none shadow-sm hover:shadow-md transition-all group overflow-hidden ${enrolled === false ? "border-slate-200 opacity-60" : "border-slate-200 hover:border-slate-400"}`}>
          <div className="h-32 overflow-hidden bg-slate-100">
            <img src={course.thumbnail || thumbnails[idx % thumbnails.length]} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </div>
          <CardContent className="p-4">
            <div className="flex gap-1 mb-2 flex-wrap">
              <Badge variant={course.status === "published" ? "default" : "secondary"} className="rounded-none text-xs capitalize">{course.status}</Badge>
              {course.is_free && <Badge className="rounded-none text-xs bg-emerald-100 text-emerald-700 border-0">Free</Badge>}
              {!course.programme_id && <Badge variant="outline" className="rounded-none text-xs">Standalone</Badge>}
            </div>
            <h3 className="text-sm font-semibold text-slate-900 mb-1 group-hover:text-blue-800 transition-colors leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{course.title}</h3>
            <p className="text-xs text-slate-500 line-clamp-2 mb-2">{course.description}</p>
            {course.instructor_name && (
              <div className="flex items-center gap-1 text-xs text-slate-500"><User size={12} /> {course.instructor_name}</div>
            )}
          </CardContent>
        </Card>
      </Link>
      {canCreate && (
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity z-10">
          <button onClick={(e) => { e.preventDefault(); navigate(`/courses/${course.id}`); }} className="bg-white/90 border border-slate-200 p-1.5 hover:bg-blue-50 text-slate-500 hover:text-blue-700 shadow-sm" data-testid={`edit-course-card-${idx}`}>
            <PencilSimple size={14} />
          </button>
          <button onClick={(e) => handleDeleteCourse(course.id, e)} className="bg-white/90 border border-slate-200 p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-600 shadow-sm" data-testid={`delete-course-card-${idx}`}>
            <Trash size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function CourseFoldersAdmin({ courses, programmes, navigate, handleDeleteCourse, thumbnails }) {
  // Group courses: Free folder + one folder per programme + Standalone (unassigned) folder.
  const freeCourses = courses.filter(c => c.is_free);
  const paidUnassigned = courses.filter(c => !c.is_free && !c.programme_id);
  const byProgramme = {};
  programmes.forEach(p => { byProgramme[p.id] = []; });
  courses.forEach(c => {
    if (!c.is_free && c.programme_id && byProgramme[c.programme_id]) {
      byProgramme[c.programme_id].push(c);
    }
  });

  // Round 35 — folder order: Free → Standalone Paid → Programmes (sorted by code).
  const sortedProgrammes = [...programmes].sort(
    (a, b) => (a.code || "").localeCompare(b.code || "")
  );
  const folders = [
    { id: "free", label: "Free Courses", icon: "free", items: freeCourses },
    { id: "standalone", label: "Standalone Paid Courses", icon: "alone", items: paidUnassigned },
    ...sortedProgrammes.map(p => ({ id: p.id, label: p.title, icon: "prog", items: byProgramme[p.id] || [] })),
  ];

  // Round 35 — persist open-folder state to sessionStorage so navigating
  // into a course and back restores the same expanded folders + scroll.
  const SESSION_KEY = "courses_openFolders";
  const [openFolders, setOpenFolders] = useState(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    const init = { free: true };
    if (programmes[0]) init[programmes[0].id] = true;
    return init;
  });
  useEffect(() => {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(openFolders)); } catch (_) {}
  }, [openFolders]);
  const toggle = (id) => setOpenFolders(s => ({ ...s, [id]: !s[id] }));

  return (
    <div className="space-y-2" data-testid="course-folders">
      {folders.map(f => {
        const isOpen = !!openFolders[f.id];
        return (
          <div key={f.id} className="border border-slate-200 bg-white">
            <button
              onClick={() => toggle(f.id)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
              data-testid={`folder-toggle-${f.id}`}
            >
              <div className="flex items-center gap-2">
                {isOpen ? <CaretDown size={14} weight="bold" className="text-slate-500" /> : <CaretRight size={14} weight="bold" className="text-slate-500" />}
                <FolderOpen size={16} weight="fill" className={f.icon === "free" ? "text-emerald-600" : "text-blue-700"} />
                <span className="font-semibold text-sm text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>{f.label}</span>
                <span className="text-xs text-slate-400">({f.items.length})</span>
              </div>
            </button>
            {isOpen && (
              <div className="px-4 py-3 border-t border-slate-100 bg-slate-50">
                {f.items.length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-2">No courses in this folder yet.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {f.items.map((course, idx) => (
                      <CourseCard key={course.id} course={course} idx={idx} canCreate={true} navigate={navigate} handleDeleteCourse={handleDeleteCourse} thumbnails={thumbnails} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function CoursesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [myLearning, setMyLearning] = useState(null);
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", programme_id: "", status: "draft", is_free: false,
    course_type: "standalone", price: 0, sequence_order: 0,
    bank_name: "", bank_account_name: "", bank_account_number: "", bank_swift: "", bank_instructions: ""
  });
  const [saving, setSaving] = useState(false);
  const canCreate = ["super_admin", "admin", "instructor"].includes(user?.role);
  const isStudent = user?.role === "student";

  useEffect(() => {
    if (canCreate) {
      api.get("/courses").then(r => { setCourses(r.data); setLoading(false); }).catch(() => setLoading(false));
      api.get("/programmes").then(r => setProgrammes(r.data)).catch(() => {});
    } else {
      // Student: fetch only enrolled courses
      api.get("/courses/my-learning").then(r => { setMyLearning(r.data); setLoading(false); }).catch(() => setLoading(false));
    }
  }, [canCreate]);

  // Round 35 — restore scroll position after loading completes (one-shot).
  useEffect(() => {
    if (loading) return;
    try {
      const y = parseInt(sessionStorage.getItem("courses_scrollY") || "0", 10);
      if (y > 0) {
        window.scrollTo(0, y);
        sessionStorage.removeItem("courses_scrollY");
      }
    } catch (_) {}
  }, [loading]);

  const handleCreate = async () => {
    const isStandalone = form.course_type !== "with_programme";
    const payload = {
      title: form.title, description: form.description,
      programme_id: form.course_type === "with_programme" ? form.programme_id : "",
      status: form.status, is_free: form.is_free,
      price: form.is_free ? 0 : (form.price || 0),
      sequence_order: form.course_type === "with_programme" ? parseInt(form.sequence_order, 10) || 0 : 0,
      bank_name: isStandalone && !form.is_free ? form.bank_name : "",
      bank_account_name: isStandalone && !form.is_free ? form.bank_account_name : "",
      bank_account_number: isStandalone && !form.is_free ? form.bank_account_number : "",
      bank_swift: isStandalone && !form.is_free ? form.bank_swift : "",
      bank_instructions: isStandalone && !form.is_free ? form.bank_instructions : "",
    };
    setSaving(true);
    try {
      await api.post("/courses", payload);
      toast.success("Course created");
      setDialogOpen(false);
      setForm({
        title: "", description: "", programme_id: "", status: "draft", is_free: false,
        course_type: "standalone", price: 0, sequence_order: 0,
        bank_name: "", bank_account_name: "", bank_account_number: "", bank_swift: "", bank_instructions: ""
      });
      api.get("/courses").then(r => setCourses(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create course"); }
    setSaving(false);
  };

  const handleDeleteCourse = async (courseId, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm("Delete this course? This cannot be undone.")) return;
    try {
      await api.delete(`/courses/${courseId}`);
      toast.success("Course deleted");
      setCourses(prev => prev.filter(c => c.id !== courseId));
    } catch { toast.error("Failed to delete course"); }
  };

  const thumbnails = [
    "https://static.prod-images.emergentagent.com/jobs/8f8a1e34-b7b5-4fc0-b7ca-0a56778fb2cb/images/d4b8ffef887e197514a47fb2cd962b1a02d68cf2bf3b5466179d8609bf49d9d5.png",
    "https://static.prod-images.emergentagent.com/jobs/8f8a1e34-b7b5-4fc0-b7ca-0a56778fb2cb/images/b27ca7caff0b63e0d8ae467ae6050379e9a9fc8099d017bb975b8953d134a745.png",
  ];

  // Group enrolled courses by programme for student view
  const enrolledByProgramme = {};
  if (myLearning) {
    for (const e of (myLearning.enrollments || [])) {
      enrolledByProgramme[e.programme_id] = e;
    }
  }
  const enrolledProgrammeIds = Object.keys(enrolledByProgramme);
  const enrolledCoursesByProg = {};
  if (myLearning) {
    for (const c of (myLearning.enrolled_courses || [])) {
      if (!enrolledCoursesByProg[c.programme_id]) enrolledCoursesByProg[c.programme_id] = [];
      enrolledCoursesByProg[c.programme_id].push(c);
    }
    // Sort each programme's courses by sequence_order so the numbered unlock
    // chain (course #1 → #2 → #3) is visually consistent with what the
    // student sees on the programme overview page.
    for (const pid of Object.keys(enrolledCoursesByProg)) {
      enrolledCoursesByProg[pid].sort((a, b) =>
        ((a.sequence_order ?? a.order ?? 9999) - (b.sequence_order ?? b.order ?? 9999))
        || (a.title || "").localeCompare(b.title || "")
      );
    }
  }
  // If MyProgrammes → "Go to Courses" passed ?programme=X, scroll the matching
  // programme section into view once the page settles.
  const programmeFilter = (typeof window !== "undefined") ? new URLSearchParams(window.location.search).get("programme") : null;
  useEffect(() => {
    if (!programmeFilter) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid='enrolled-programme-${programmeFilter}']`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
    return () => clearTimeout(t);
  }, [programmeFilter, loading]);

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="courses-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">{isStudent ? "My Learning" : "Courses"}</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {canCreate ? "Course Management" : "My Learning"}
            </h1>
          </div>
          {canCreate && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="create-course-btn">
                  <Plus size={16} weight="bold" /> New Course
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md rounded-none max-h-[90vh] overflow-y-auto" data-testid="create-course-dialog">
                <DialogHeader>
                  <DialogTitle className="text-xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>Create Course</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-3 pb-2">
                  <div><Label className="text-sm font-medium text-slate-700">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 rounded-none" data-testid="course-title-input" /></div>
                  <div><Label className="text-sm font-medium text-slate-700">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1 rounded-none" rows={3} /></div>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={form.is_free} onCheckedChange={v => setForm({...form, is_free: v, price: v ? 0 : form.price})} data-testid="course-free-checkbox" />
                    <Label className="text-sm text-slate-600 cursor-pointer">This is a free course</Label>
                  </div>
                  {!form.is_free && (
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Course Price (USD)</Label>
                      <Input type="number" value={form.price || ""} onChange={e => setForm({...form, price: parseFloat(e.target.value) || 0})} className="mt-1 rounded-none" placeholder="e.g. 100" data-testid="course-price-input" />
                      <p className="text-xs text-slate-400 mt-0.5">Leave 0 or blank for programme-bundled courses</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Course Type</Label>
                    <Select value={form.course_type} onValueChange={v => setForm({...form, course_type: v})}>
                      <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standalone">Standalone Course</SelectItem>
                        <SelectItem value="with_programme">Part of a Programme</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Bank details — only for paid standalone courses */}
                  {!form.is_free && form.course_type === "standalone" && (
                    <div className="bg-slate-50 border border-slate-200 p-3 space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-widest text-slate-600">Bank Transfer Details (Optional)</p>
                      <p className="text-[11px] text-slate-500 -mt-1">Students will see these bank details when paying via manual transfer for this course.</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-slate-600">Bank Name</Label>
                          <Input value={form.bank_name} onChange={e => setForm({...form, bank_name: e.target.value})} className="mt-0.5 rounded-none h-9 text-sm" placeholder="e.g. Providus Bank" data-testid="course-bank-name" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Account Name</Label>
                          <Input value={form.bank_account_name} onChange={e => setForm({...form, bank_account_name: e.target.value})} className="mt-0.5 rounded-none h-9 text-sm" placeholder="e.g. SBL Ltd" data-testid="course-bank-account-name" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Account Number</Label>
                          <Input value={form.bank_account_number} onChange={e => setForm({...form, bank_account_number: e.target.value})} className="mt-0.5 rounded-none h-9 text-sm" placeholder="10-digit number" data-testid="course-bank-account-number" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">SWIFT / Sort Code</Label>
                          <Input value={form.bank_swift} onChange={e => setForm({...form, bank_swift: e.target.value})} className="mt-0.5 rounded-none h-9 text-sm" placeholder="Optional" data-testid="course-bank-swift" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Payment Instructions</Label>
                        <Textarea value={form.bank_instructions} onChange={e => setForm({...form, bank_instructions: e.target.value})} className="mt-0.5 rounded-none text-sm" rows={2} placeholder="e.g. Use your full name as reference and email the slip…" data-testid="course-bank-instructions" />
                      </div>
                    </div>
                  )}
                  {form.course_type === "with_programme" && (
                    <>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Programme</Label>
                        <Select value={form.programme_id} onValueChange={v => setForm({...form, programme_id: v})}>
                          <SelectTrigger className="mt-1 rounded-none" data-testid="course-programme-select"><SelectValue placeholder="Select programme" /></SelectTrigger>
                          <SelectContent>{programmes.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Order # in programme</Label>
                        <Input type="number" min="0" value={form.sequence_order} onChange={e => setForm({...form, sequence_order: e.target.value})} className="mt-1 rounded-none" data-testid="course-sequence-order-input" />
                        <p className="text-[11px] text-slate-500 mt-1">Course 1 is unlocked first; later courses unlock when the previous one is complete.</p>
                      </div>
                    </>
                  )}
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Status</Label>
                    <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                      <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleCreate} disabled={saving || !form.title || (form.course_type === "with_programme" && !form.programme_id)} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="save-course-btn">
                    {saving ? "Creating..." : "Create Course"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => <div key={i} className="h-48 bg-slate-100 animate-pulse" />)}
          </div>
        ) : canCreate ? (
          /* Admin/Instructor: organized into expandable folders by programme + Free Courses */
          courses.length === 0 ? (
            <div className="text-center py-16"><Books size={48} className="text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No courses available</p></div>
          ) : (
            <CourseFoldersAdmin
              courses={courses}
              programmes={programmes}
              navigate={navigate}
              handleDeleteCourse={handleDeleteCourse}
              thumbnails={thumbnails}
            />
          )
        ) : (
          /* Student view: enrolled programmes then free courses */
          <div className="space-y-8">
            {enrolledProgrammeIds.length === 0 && (!myLearning?.free_courses || myLearning.free_courses.length === 0) && (
              <div className="text-center py-16">
                <Books size={48} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">You haven't enrolled in any courses yet.</p>
                <Link to="/programmes-catalogue"><Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none">Browse Programmes</Button></Link>
              </div>
            )}

            {enrolledProgrammeIds.map(progId => {
              const enrollment = enrolledByProgramme[progId];
              const progCourses = enrolledCoursesByProg[progId] || [];
              const isPending = enrollment.status === "pending_payment";
              return (
                <div key={progId} data-testid={`enrolled-programme-${progId}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <GraduationCap size={20} className="text-blue-800" />
                    <div>
                      <h2 className="text-lg font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {enrollment.programme_title || "Programme"}
                      </h2>
                      <div className="flex items-center gap-2">
                        <Badge className={`rounded-none text-xs ${isPending ? "bg-amber-500 text-white" : enrollment.balance <= 0 ? "bg-emerald-600 text-white" : ""}`}>
                          {isPending ? "Payment Pending" : enrollment.balance <= 0 ? "Paid" : "Active"}
                        </Badge>
                        {isPending && <span className="text-xs text-amber-600 flex items-center gap-1"><Lock size={11} /> Content locked until payment confirmed</span>}
                      </div>
                    </div>
                  </div>
                  {progCourses.length === 0 ? (
                    <p className="text-sm text-slate-400 ml-8">No courses available for this programme yet.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ml-0">
                      {progCourses.map((course, idx) => (
                        <CourseCard key={course.id} course={course} idx={idx} canCreate={false} navigate={navigate} handleDeleteCourse={handleDeleteCourse} thumbnails={thumbnails} enrolled={!isPending} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {myLearning?.free_courses?.length > 0 && (
              <div data-testid="free-courses-section">
                <div className="flex items-center gap-3 mb-4">
                  <Books size={20} className="text-emerald-600" />
                  <h2 className="text-lg font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Free Courses</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myLearning.free_courses.map((course, idx) => (
                    <CourseCard key={course.id} course={course} idx={idx} canCreate={false} navigate={navigate} handleDeleteCourse={handleDeleteCourse} thumbnails={thumbnails} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
